import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryCostLayer } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-cost-layer-detail',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, RouterLink, ButtonModule],
  template: `
    <ain-page [title]="t('catalog.costing.layers.detailTitle')" [description]="t('catalog.costing.layers.detailDescription')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.layers.detailHelp.title'"
        [descriptionKey]="'catalog.costing.layers.detailHelp.description'" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!layer()) {
        <ain-state type="error" [title]="t('catalog.costing.layers.notFound')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <a [routerLink]="['/inventory/costing/layers']" class="back-link">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>
          <div class="summary-grid">
            <div><strong>{{ t('catalog.inventory.offeringId') }}:</strong> {{ resolveOffering(layer()!.offeringId) }}</div>
            <div><strong>{{ t('catalog.inventory.warehouseId') }}:</strong> {{ resolveWarehouse(layer()!.warehouseId) }}</div>
            <div><strong>{{ t('catalog.costing.layers.originalQty') }}:</strong> {{ layer()!.originalQuantity }}</div>
            <div><strong>{{ t('catalog.costing.layers.remainingQty') }}:</strong> {{ layer()!.remainingQuantity }}</div>
            <div><strong>{{ t('catalog.costing.layers.unitCost') }}:</strong> {{ layer()!.unitCost }} {{ layer()!.currencyCode }}</div>
            <div><strong>{{ t('catalog.costing.layers.receivedAt') }}:</strong> {{ layer()!.receivedAtUtc }}</div>
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .toolbar { display: flex; gap: 8px; margin-bottom: 12px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .summary-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
  `]
})
export class InventoryCostLayerDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly layer = signal<InventoryCostLayer | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  private layerId = '';

  ngOnInit(): void {
    this.layerId = this.route.snapshot.paramMap.get('id') ?? '';
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.costingApi.getCostLayer(this.layerId).subscribe({
      next: layer => {
        this.layer.set(layer);
        this.loading.set(false);
      },
      error: () => {
        this.layer.set(null);
        this.loading.set(false);
      }
    });
  }

  resolveOffering(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : '-';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
